import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import { ICompanyGroupListItem } from "../../../models/companyGroup/companyGroupListItem";
import { ICompanyGroupListItems } from "../../../models/companyGroup/companyGroupListItems";
import * as companyGroupService from "../../../models/services/companyGroupService";
import { ActionContainer } from "../../framework/ActionContainer";
import { Dialog } from "../../framework/Dialog/Dialog";
import { List } from "../../framework/List/List";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface ISelectCompanyGroupDialogProps {
    classes?: string;
    companyId: string;
    records?: ICompanyGroupListItems;
    ignoreCompanyGroupIds?: string[];
    multiSelect?: boolean;
    onOk: (userGroups: ICompanyGroupListItem[]) => void;
    onCancel: () => void;
}

export interface ISelectCompanyGroupDialogState {
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;
    pageSize: number;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: ICompanyGroupListItem[];
    selectedId: string;
    checkedIds: string[];
}

export class SelectCompanyGroupDialog extends React.Component<ISelectCompanyGroupDialogProps, ISelectCompanyGroupDialogState> {
    constructor(props) {
        super(props);
        this.state = {
            intialized: !!props.records,
            isLoading: false,
            waitingPreloadedData: !props.records,
            pageSize: props.records ? props.records.pageSize : infiniteListPageSize,
            page: props.records ? props.records.page : 1,
            hasMore: props.records ? props.records.hasMore : true,
            filter: "",
            sortColumn: "name",
            sortOrderIsAsc: true,
            items: props.records ? props.records.items : [],
            selectedId: null,
            checkedIds: [],
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean) => {
        const props = this.props;
        const state = this.state;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => companyGroupService.getCompanyGroupListItems(!refreshList ? pageSize : (page * infiniteListPageSize), !refreshList ? page : 1, filter, props.ignoreCompanyGroupIds, sortColumn, sortOrderIsAsc),
            false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId: items.selectedId,
            checkedIds: items.checkedIds,
        });
    };

    componentDidMount(): void {
        const state = this.state;
        if (this.props.records) return;
        this.searchItems(state.pageSize, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    componentDidUpdate(prevProps: ISelectCompanyGroupDialogProps, prevState: ISelectCompanyGroupDialogState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (!props.records) return;
            this.setState({
                intialized: true,
                waitingPreloadedData: false,
                page: props.records.page,
                hasMore: props.records.hasMore,
                items: props.records.items,
                pageSize: props.records.pageSize,
                sortColumn: props.records.sortColumn,
                sortOrderIsAsc: props.records.sortOrderIsAsc
            });
        } else {
            const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
            const companyId = props.companyId ? props.companyId : "";
            if (companyId === prevCompanyId && Base.equalStrArrays(prevProps.ignoreCompanyGroupIds, props.ignoreCompanyGroupIds)) return;
            this.searchItems(state.pageSize, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
        }
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    handleCancel = () => {
        this.props.onCancel();
    };

    handleOk = () => {
        const state = this.state;
        const ids = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (ids.length < 1) return;
        const items = state.items.filter(i => ids.indexOf(i.id) > -1);
        if (items.length < 1) return;
        this.props.onOk(items);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="select-company-group-dialog"
                maxWidth="md"
                maxHeight="726px"
                title={Translations.SelectCompanyGroup}
                body={
                    <VerticalContainer
                        before={<>
                            <ActionContainer
                                leftItems={
                                    <ListSearchFilter
                                        searchFilter={state.filter}
                                        onSearchClick={this.changeFilter} />
                                }
                            />
                        </>}
                        main={<>
                            <List
                                height="100%"
                                multiSelect={props.multiSelect}
                                intialized={state.intialized}
                                isLoading={state.isLoading}
                                hasMore={state.hasMore}
                                header={
                                    {
                                        columns: [
                                            { size: 4, column: "code", content: Translations.Code },
                                            { size: 4, column: "name", content: Translations.Name },
                                            { size: 4, column: "comment", content: Translations.Description }
                                        ]
                                    }
                                }
                                lines={
                                    state.items.map((item) => {
                                        return {
                                            columns: [
                                                { size: 4, column: "code", content: item.code },
                                                { size: 4, column: "name", content: item.name },
                                                { size: 4, column: "comment", content: item.comment }
                                            ],
                                            id: item.id
                                        };
                                    })}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                            />
                        </>}
                    />
                }
                buttons={[
                    { title: Translations.Cancel, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: this.handleOk },
                ]}
                onClose={this.handleCancel}
            />
        );
    }
}